import type { MerchantApiClient } from "../../types.js";
import type {
  RoleAssignmentDraft,
  StatusChangeDraft,
  TeamAccessFilters,
  TeamAccessPage,
  TeamUser,
} from "./models.js";

interface DataEnvelope<T> {
  data: T;
  meta: { requestId: string };
}

interface ListEnvelope<T> {
  items: T[];
  nextCursor: string | null;
  meta: { requestId: string };
}

export interface TeamAccessApi {
  assignRole(userId: string, draft: RoleAssignmentDraft, idempotencyKey: string): Promise<TeamUser>;
  getUser(userId: string): Promise<TeamUser>;
  listUsers(filters: TeamAccessFilters): Promise<TeamAccessPage>;
  updateStatus(userId: string, draft: StatusChangeDraft, idempotencyKey: string): Promise<TeamUser>;
}

export function createTeamAccessApi(client: MerchantApiClient): TeamAccessApi {
  return {
    async assignRole(userId, draft, idempotencyKey) {
      return unwrap(
        await client.patch<DataEnvelope<TeamUser>>(
          `/v1/api/users/${encodeURIComponent(userId)}/role`,
          {
            role: draft.role,
            reason: draft.reason,
          },
          { idempotencyKey },
        ),
      );
    },
    async getUser(userId) {
      return unwrap(
        await client.get<DataEnvelope<TeamUser>>(`/v1/api/users/${encodeURIComponent(userId)}`),
      );
    },
    async listUsers(filters) {
      const envelope = await client.get<ListEnvelope<TeamUser>>(`/v1/api/users?${query(filters)}`);
      return { users: envelope.items, nextCursor: envelope.nextCursor };
    },
    async updateStatus(userId, draft, idempotencyKey) {
      return unwrap(
        await client.patch<DataEnvelope<TeamUser>>(
          `/v1/api/users/${encodeURIComponent(userId)}/status`,
          {
            status: draft.status,
            reason: draft.reason,
          },
          { idempotencyKey },
        ),
      );
    },
  };
}

function unwrap<T>(envelope: DataEnvelope<T>): T {
  return envelope.data;
}

function query(filters: TeamAccessFilters): string {
  return new URLSearchParams(
    Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== "")
      .map(([key, value]) => [key, String(value)]),
  ).toString();
}
